// Shared browser audio unlock for phones, televisions, Steam Deck game mode,
// installed PWAs and desktop browsers. Those environments may suspend every
// Web Audio context until a real touch, click, or controller key is received.
// Once the user has unlocked audio once, we remember it so the next page load
// can restore sound on the very first gesture instead of asking again.

type UnlockableContext = AudioContext & { state: AudioContextState };

const contexts = new Set<UnlockableContext>();
let armed = false;

const UNLOCKED_KEY = "sophia:audio:unlocked";

/** True when sound was unlocked in *this* tab, so a reload needs no new tap. */
export function isAudioUnlockedThisTab(): boolean {
  try { return typeof sessionStorage !== "undefined" && sessionStorage.getItem(UNLOCKED_KEY) === "1"; }
  catch { return false; }
}

export function isAudioUnlocked(): boolean {
  if (isAudioUnlockedThisTab()) return true;
  try { return typeof localStorage !== "undefined" && localStorage.getItem(UNLOCKED_KEY) === "1"; }
  catch { return false; }
}

export function setAudioUnlocked(on: boolean): void {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(UNLOCKED_KEY, on ? "1" : "0"); }
  catch { /* quota / private mode */ }
  try {
    if (typeof sessionStorage === "undefined") return;
    if (on) sessionStorage.setItem(UNLOCKED_KEY, "1");
    else sessionStorage.removeItem(UNLOCKED_KEY);
  } catch { /* quota / private mode */ }
}

export function registerAudioContext(context: AudioContext): () => void {
  contexts.add(context);
  return () => contexts.delete(context);
}

export async function unlockAppAudio(): Promise<boolean> {
  const jobs = Array.from(contexts, async (context) => {
    if (context.state === "suspended") await context.resume();
    return context.state === "running";
  });

  const media = Array.from(document.querySelectorAll<HTMLMediaElement>("[data-sophia-audible='true']"));
  for (const element of media) {
    element.muted = false;
    if (element.volume === 0) element.volume = 1;
  }

  const results = await Promise.allSettled(jobs);
  setAudioUnlocked(true);
  window.dispatchEvent(new CustomEvent("sophia:audio-unlocked"));
  return results.every((result) => result.status === "fulfilled");
}

export function markAudible(element: HTMLMediaElement): void {
  element.dataset.sophiaAudible = "true";
  element.muted = false;
  if (element.volume === 0) element.volume = 1;
}

export function bindAudioUnlock(): () => void {
  if (armed) return () => {};
  armed = true;
  const unlock = () => {
    // Resume every app-created context. Native media keeps its own controls,
    // but anything explicitly marked as audible is also released from mute.
    void unlockAppAudio();
    void import("./audioOut").then((audio) => audio.availablePreferredSink()).catch(() => null);
  };
  const events: Array<keyof WindowEventMap> = ["pointerdown", "touchstart", "keydown"];
  events.forEach((event) => window.addEventListener(event, unlock, { capture: true, passive: true }));
  return () => {
    events.forEach((event) => window.removeEventListener(event, unlock, { capture: true }));
    armed = false;
  };
}

/** Forget the remembered audio permission (e.g. after explicit sign-out). */
export function resetAudioUnlock(): void {
  setAudioUnlocked(false);
}